import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import { useLocation, useNavigate } from "react-router-dom";
import { studentBatchTransfer } from "../../api/Student";
import { batchDetailsWithStudents, batchList } from "../../api/Batch";
import Master from "./Master";

export default function StudentBatchTransfer() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // ----------------------------- State -----------------------------
  const [loading, setLoading] = useState("");
  const [students, setStudents] = useState([]);
  const [student, setStudent] = useState({});

  const [filters1, setFilters1] = useState({});
  const [filters2, setFilters2] = useState({});
  const [batches, setBatches] = useState([]);
  const formRef = useRef(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [keepOldFee, setKeepOldFee] = useState(true);
  const feeOption = keepOldFee ? "old" : "new";

  const handleSubjectToggle = (id) => {
    setSelectedSubjects((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };


  // ----------------------------- Effects -----------------------------

  useEffect(() => {
    const batchParam = params.get("batch");
    const studentParam = params.get("student_id");
    const batchNFeeParam = params.get("batch_n_fee_id");
    setFilters1({
      ...filters1,
      batch_id: batchParam ? Number(batchParam) : "",
      student_id: studentParam ? Number(studentParam) : "",
      batch_n_fee_id: batchNFeeParam ? Number(batchNFeeParam) : "",
    });
  }, []);

  const getBatchList = async () => {
    setLoading(true);
    try {
      const response = await batchList(`limit=all`);
      if (response.data?.status) {
        setBatches(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBatchList();
  }, []);

  const getBatchStudents = async () => {
    setLoading(true);
    try {
      let addUrl = `id=${filters1?.batch_id}`;
      const response = await batchDetailsWithStudents(addUrl);
      if (response.data.status) {
        const activeStudents = response?.data?.data?.active_batch_students ?? [];
        setStudents(activeStudents);


        let preselected = null;
        if (filters1?.batch_n_fee_id) {
          preselected = activeStudents.find(
            (f) => Number(f?.id) === Number(filters1?.batch_n_fee_id)
          );
        } else if (filters1?.student_id) {
          preselected = activeStudents.find(
            (f) => Number(f?.student_id) === Number(filters1?.student_id)
          );
        }

        if (preselected) {
          setStudent(preselected);
          setFilters1((prev) => ({ ...prev, batch_n_fee_id: preselected?.id }));
        }
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters1?.batch_id) {
      getBatchStudents();
    }
  }, [filters1?.batch_id]);

  const handleForm = async (e) => {
    e.preventDefault();
    if (selectedSubjects.length === 0) {
      SweetAlertShow(
        "error",
        "Validation Error",
        "Please select at least one subject."
      );
      return;
    }


    if (!filters2?.batch_id) {
      SweetAlertShow(
        "error",
        "Validation Error",
        "Please select destination batch."
      );
      return;
    }

    if (!student?.id) {
      SweetAlertShow(
        "error",
        "Validation Error",
        "Please select student."
      );
      return;
    }

    if (!keepOldFee && paidMoreThanNewFee) {
      SweetAlertShow(
        "error",
        "Fee Validation Error",
        `Student has already paid ₹${paidFee}, but new batch fee is ₹${newBatchFee}. New batch fee cannot be less than paid fee.`
      );
      return;
    }

    if (!keepOldFee && hasMissingPaidFeeType) {
      SweetAlertShow(
        "error",
        "Fee Type Validation Error",
        `Paid fee type ${missingPaidFeeTypes.join(", ")} is not available in the new batch. Please keep the old batch fee or select a valid destination batch.`
      );
      return;
    }

    if (!keepOldFee && hasMissingPaidFeeType) {
      SweetAlertShow(
        "error",
        "Fee Type Validation Error",
        `Paid fee type ${missingPaidFeeTypes.join(", ")} is not available in the new batch. Please keep the old batch fee or select a valid destination batch.`
      );
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData(e.target);
      const response = await studentBatchTransfer(formData);
      if (response?.data?.status) {
        e.target.reset();
        navigate(`/student/details?id=${student?.student_id}`);
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters1?.batch_n_fee_id) {
      setStudent(
        students?.find(
          (f) => Number(f?.id) === Number(filters1?.batch_n_fee_id)
        )
      );
    }
  }, [filters1?.batch_n_fee_id, students]);

  const currentBatch = useMemo(
    () => batches?.find((b) => b?.id === Number(filters1?.batch_id)),
    [batches, filters1?.batch_id]
  );

  const destinationBatch = useMemo(
    () => batches?.find((b) => b?.id === Number(filters2?.batch_id)),
    [batches, filters2?.batch_id]
  );

  // ================= Fee Calculation =================

  const oldBatchFee = Number(student?.final_fee ?? 0);

  const newBatchFee = Number(
    destinationBatch?.total_fees ??
    destinationBatch?.final_fee ??
    destinationBatch?.total_fee ??
    destinationBatch?.fee ??
    0
  );

  const paidFee = Number(student?.total_paid ?? 0);

  // ================= Fee Type Validation =================

  const oldStudentFeeDetails = student?.student_fee_details ?? [];
  const newBatchFeeDetails = destinationBatch?.fee_details ?? [];

  // Old batch me jis fee type me payment hui hai
  const paidFeeTypes = oldStudentFeeDetails
    .filter((fee) => Number(fee?.paid_amount ?? 0) > 0)
    .map((fee) => fee?.fee_type)
    .filter(Boolean);

  // New batch ke available fee types
  const newBatchFeeTypes = newBatchFeeDetails
    .map((fee) => fee?.fee_type)
    .filter(Boolean);

  // Paid fee types jo new batch me available nahi hain
  const missingPaidFeeTypes = paidFeeTypes.filter(
    (type) => !newBatchFeeTypes.includes(type)
  );

  const hasMissingPaidFeeType = missingPaidFeeTypes.length > 0;

  // ================= Fee Type Amount Validation =================

  const feeTypeAmountErrors = oldStudentFeeDetails
    .filter((oldFee) => Number(oldFee?.paid_amount ?? 0) > 0)
    .map((oldFee) => {
      const newFee = newBatchFeeDetails.find(
        (fee) => fee?.fee_type === oldFee?.fee_type
      );

      if (!newFee) return null;

      const paidAmount = Number(oldFee?.paid_amount ?? 0);

      const newAmount = Number(
        newFee?.amount ??
        newFee?.final_amount ??
        newFee?.fee_amount ??
        0
      );

      if (paidAmount > newAmount) {
        return {
          fee_type: oldFee?.fee_type,
          paid_amount: paidAmount,
          new_amount: newAmount,
        };
      }

      return null;
    })
    .filter(Boolean);

  const hasFeeTypeAmountError = feeTypeAmountErrors.length > 0;

  // Final validation
  const newFeeTransferNotAllowed =
    !keepOldFee &&
    (
      paidFee > newBatchFee ||
      hasMissingPaidFeeType ||
      hasFeeTypeAmountError
    );


  const isNewBatchFeeSelected = !keepOldFee;

  const paidMoreThanNewFee =
    isNewBatchFeeSelected && paidFee > newBatchFee;

  const isActive =
    student?.student_info?.status === "Active" ||
    student?.student_info?.is_active === true ||
    student?.status === "Active";

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* ---------------------------- Page Header ---------------------------- */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                      <div>
                        <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                          <i className="ri-shuffle-line text-primary"></i>
                          Student Batch Transfer
                        </h4>
                        <small className="text-muted">
                          Move a student from their current batch to a new batch
                        </small>
                      </div>

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => navigate(-1)}
                      >
                        <i className="ri-arrow-left-line me-1"></i>
                        Back
                      </button>
                    </div>

                    <div className="row g-4">

                      <div className="col-12 col-xl-6">
                        {/* --------------------- Batch / Student Selection --------------------- */}
                        <div className="card shadow-sm border-0 rounded-3 mb-4">
                          <div className="card-header bg-label-primary d-flex align-items-center gap-2 py-3">
                            <i className="ri-group-line fs-5"></i>
                            <h6 className="mb-0 fw-semibold">Current Batch &amp; Student</h6>
                          </div>
                          <div className="card-body">
                            <div className="row g-3">
                              {/* Batch dropdown */}
                              <div className="col-12">
                                <label htmlFor="source_batch_id" className="form-label fw-medium">
                                  <i className="ri-calendar-2-line me-1 text-primary"></i>
                                  Select Batch
                                </label>
                                <select
                                  id="source_batch_id"
                                  className="form-select"
                                  value={filters1?.batch_id ?? ""}
                                  onChange={(e) =>
                                    setFilters1({
                                      ...filters1,
                                      batch_id: e.target.value,
                                      // switching batch manually invalidates any pre-filled student
                                      student_id: "",
                                      batch_n_fee_id: "",
                                    })
                                  }
                                >
                                  <option value="" disabled>
                                    Select Batch
                                  </option>
                                  {batches?.map((batch, i) => (
                                    <option key={i} value={batch?.id}>
                                      {batch?.title} ({batch?.course?.title})
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Student dropdown */}
                              <div className="col-12">
                                <label htmlFor="batch_n_fee_id" className="form-label fw-medium">
                                  <i className="ri-user-search-line me-1 text-primary"></i>
                                  Select Student
                                </label>
                                <select
                                  id="batch_n_fee_id"
                                  className="form-select"
                                  value={filters1?.batch_n_fee_id ?? ""}
                                  disabled={!filters1?.batch_id}
                                  onChange={(e) =>
                                    setFilters1({
                                      ...filters1,
                                      batch_n_fee_id: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">Select Student</option>
                                  {students?.map((s, i) => (
                                    <option key={i} value={s?.id}>
                                      {s?.student_info?.name} ({s?.student_info?.registration_no})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* --------------------- Student Information Card --------------------- */}
                        {student?.id && (
                          <div className="card shadow-sm border-0 rounded-3 mb-4">
                            <div className="card-header bg-label-primary d-flex align-items-center gap-2 py-3">
                              <i className="ri-user-3-line fs-5"></i>
                              <h6 className="mb-0 fw-semibold">Student Information</h6>
                            </div>
                            <div className="card-body">


                              <div className="row g-3">
                                <div className="col-6 col-md-4">
                                  <small className="text-muted d-block">Student</small>
                                  <span className="fw-semibold">
                                    {student?.student_info?.name || "—"}
                                  </span>
                                </div>

                                <div className="col-6 col-md-4">
                                  <small className="text-muted d-block">Registration No.</small>
                                  <span className="fw-semibold">
                                    {student?.student_info?.registration_no || "—"}
                                  </span>
                                </div>
                                <div className="col-6 col-md-4">
                                  <small className="text-muted d-block">Father Name</small>
                                  <span className="fw-semibold">
                                    {student?.student_info?.father_name || "—"}
                                  </span>
                                </div>



                                <div className="col-6 col-md-4">
                                  <small className="text-muted d-block">Mobile</small>
                                  <span className="fw-semibold">
                                    {student?.student_info?.phone || "—"}
                                  </span>
                                </div>

                                <div className="col-6 col-md-4">
                                  <small className="text-muted d-block">RTE Status</small>
                                  <span className="fw-semibold">
                                    {student?.rte_status || "—"}
                                  </span>
                                </div>

                              </div>

                              <hr />

                              {/* Fee Summary */}
                              <h6 className="fw-semibold mb-3">
                                <i className="ri-wallet-3-line me-1 text-primary"></i>
                                Fee Summary
                              </h6>
                              <div className="row g-3 text-center">
                                <div className="col-6 col-md-4">
                                  <div className="card border-0 bg-primary-subtle h-100 rounded-3">
                                    <div className="card-body py-3">
                                      <small className="text-primary fw-semibold d-block">
                                        Total Fee
                                      </small>
                                      <h6 className="text-primary fw-bold mb-0">
                                        ₹{student?.total_fee ?? 0}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4">
                                  <div className="card border-0 bg-warning-subtle h-100 rounded-3">
                                    <div className="card-body py-3">
                                      <small className="text-warning fw-semibold d-block">
                                        Discount
                                      </small>
                                      <h6 className="text-warning fw-bold mb-0">
                                        ₹{student?.total_discount ?? 0}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-4">
                                  <div className="card border-0 bg-info-subtle h-100 rounded-3">
                                    <div className="card-body py-3">
                                      <small className="text-info fw-semibold d-block">
                                        Final Fee
                                      </small>
                                      <h6 className="text-info fw-bold mb-0">
                                        ₹{student?.final_fee ?? 0}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-6">
                                  <div className="card border-0 bg-success-subtle h-100 rounded-3">
                                    <div className="card-body py-3">
                                      <small className="text-success fw-semibold d-block">
                                        Paid Fee
                                      </small>
                                      <h6 className="text-success fw-bold mb-0">
                                        ₹{student?.total_paid ?? 0}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-6 col-md-6">
                                  <div className="card border-0 bg-danger-subtle h-100 rounded-3">
                                    <div className="card-body py-3">
                                      <small className="text-danger fw-semibold d-block">
                                        Due Fee
                                      </small>
                                      <h6 className="text-danger fw-bold mb-0">
                                        ₹{student?.total_due ?? 0}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}



                        {/* --------------------- Transfer Preview --------------------- */}
                        {student?.id && filters2?.batch_id && (
                          <div className="card shadow-sm border-0 rounded-3 mb-4">
                            <div className="card-header bg-light d-flex align-items-center gap-2 py-3">
                              <i className="ri-git-branch-line fs-5"></i>
                              <h6 className="mb-0 fw-semibold">Transfer Preview</h6>
                            </div>

                            <div className="card-body">
                              <div className="row g-3 align-items-start">

                                {/* ================= FROM ================= */}
                                <div className="col-12 col-md-5 text-center">
                                  <span className="badge bg-secondary mt-2 mb-2">
                                    FROM
                                  </span>

                                  <h6 className="mb-1">
                                    {currentBatch?.title}
                                  </h6>

                                  <small className="text-muted d-block">
                                    {currentBatch?.course?.title}
                                  </small>

                                  {/* Student Fee Types */}
                                  {!keepOldFee && (
                                    <div className="mt-4 text-start">
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <i className="ri-file-list-3-line text-primary fs-5"></i>
                                        <span className="fw-semibold text-muted">
                                          Fee Types
                                        </span>
                                      </div>

                                      {oldStudentFeeDetails.filter(
                                        (fee) => Number(fee?.paid_amount ?? 0) > 0
                                      ).length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                          {oldStudentFeeDetails
                                            .filter((fee) => Number(fee?.paid_amount ?? 0) > 0)
                                            .map((fee) => (
                                              <span
                                                key={fee?.id}
                                                className="badge bg-secondary-subtle text-secondary"
                                              >
                                                {fee?.fee_type} — ₹
                                                {Number(fee?.paid_amount ?? 0).toFixed(2)}
                                              </span>
                                            ))}
                                        </div>
                                      ) : (
                                        <small className="text-muted">
                                          No paid fee type
                                        </small>
                                      )}
                                    </div>
                                  )}
                                </div>


                                {/* ================= ARROW ================= */}
                                {!keepOldFee && (
                                  <div className="col-12 col-md-2 d-flex justify-content-center align-items-center">
                                    <div
                                      className="d-flex justify-content-center align-items-center"
                                      style={{
                                        minHeight: "150px",
                                      }}
                                    >
                                      <i className="ri-arrow-right-line fs-1 text-primary"></i>
                                    </div>
                                  </div>
                                )}


                                {/* ================= TO ================= */}
                                <div className="col-12 col-md-5 text-center">
                                  <span className="badge bg-success mt-2 mb-2">
                                    TO
                                  </span>

                                  <h6 className="mb-1">
                                    {destinationBatch?.title}
                                  </h6>

                                  <small className="text-muted d-block">
                                    {destinationBatch?.course?.title}
                                  </small>

                                  {/* New Batch Fee Types */}
                                  {!keepOldFee && (
                                    <div className="mt-4 text-start">
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <i className="ri-file-list-3-line text-success fs-5"></i>
                                        <span className="fw-semibold text-muted">
                                          Fee Types
                                        </span>
                                      </div>

                                      {newBatchFeeDetails.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                          {newBatchFeeDetails.map((fee, index) => (
                                            <span
                                              key={index}
                                              className="badge bg-primary-subtle text-primary"
                                            >
                                              {fee?.fee_type}
                                              {fee?.amount != null &&
                                                ` — ₹${Number(fee.amount).toFixed(2)}`}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <small className="text-muted">
                                          No fee type available
                                        </small>
                                      )}
                                    </div>
                                  )}
                                </div>

                              </div>
                            </div>
                          </div>
                        )}
                      </div>


                      <div className="col-12 col-xl-6">

                        <form ref={formRef} onSubmit={handleForm}>
                          {/* --------------------- Destination Batch Card --------------------- */}
                          <div className="card shadow-sm border-0 rounded-3 mb-4">
                            <div className="card-header bg-label-success d-flex align-items-center gap-2 py-3">
                              <i className="ri-send-plane-line fs-5"></i>
                              <h6 className="mb-0 fw-semibold">Transfer To</h6>
                            </div>
                            <div className="card-body">
                              <div className="row g-3">
                                <div className="col-12">
                                  <label htmlFor="dest_batch_id" className="form-label fw-medium">
                                    <i className="ri-calendar-check-line me-1 text-success"></i>
                                    Select Destination Batch
                                  </label>
                                  <select
                                    name="batch_id"
                                    id="dest_batch_id"
                                    className="form-select"
                                    value={filters2?.batch_id ?? ""}
                                    onChange={(e) =>
                                      setFilters2({
                                        ...filters2,
                                        batch_id: e.target.value,
                                      })
                                    }
                                  >
                                    <option value="" disabled>
                                      Select Batch
                                    </option>
                                    {batches?.map((batch, i) => (
                                      <option
                                        key={i}
                                        value={batch?.id}
                                        disabled={
                                          Number(filters1?.batch_id) === batch?.id
                                        }
                                      >
                                        {batch?.title} ({batch?.course?.title})
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Destination batch details */}
                                {destinationBatch && (
                                  <div className="col-12">

                                    <div className="row g-3">

                                      <div className="col-6 col-md-3">
                                        <small className="text-muted d-block">Course</small>
                                        <span className="fw-semibold">
                                          {destinationBatch?.course?.title || "—"}
                                        </span>
                                      </div>

                                      <div className="col-6 col-md-3">
                                        <small className="text-muted d-block">Strength</small>
                                        <span className="fw-semibold">
                                          {destinationBatch?.strength ?? "—"}
                                        </span>
                                      </div>

                                      <div className="col-6 col-md-3">
                                        <small className="text-muted d-block">
                                          Available Seats
                                        </small>
                                        <span className="fw-semibold">
                                          {destinationBatch?.available_seats ?? "—"}
                                        </span>
                                      </div>

                                      <div className="col-6 col-md-3">
                                        <small className="text-muted d-block">
                                          Timing
                                        </small>
                                        <span className="fw-semibold">
                                          {destinationBatch?.timing || "—"}
                                        </span>
                                      </div>

                                    </div>

                                    {/* ================= Destination Batch Fee ================= */}
                                    <hr />

                                    <div className="mt-3">

                                      <h6 className="fw-semibold mb-3">
                                        <i className="ri-wallet-3-line me-1 text-success"></i>
                                        Destination Batch Fee
                                      </h6>

                                      <div className="row g-3">

                                        <div className="col-6 col-md-4">
                                          <div className="card border-0 bg-primary-subtle h-100">
                                            <div className="card-body py-3">
                                              <small className="text-primary fw-semibold d-block">
                                                New Batch Fee
                                              </small>

                                              <h6 className="text-primary fw-bold mb-0">
                                                ₹{newBatchFee}
                                              </h6>
                                            </div>
                                          </div>
                                        </div>

                                        {/* <div className="col-6 col-md-4">
                                          <div className="card border-0 bg-success-subtle h-100">
                                            <div className="card-body py-3">
                                              <small className="text-success fw-semibold d-block">
                                                Student Paid
                                              </small>

                                              <h6 className="text-success fw-bold mb-0">
                                                ₹{paidFee}
                                              </h6>
                                            </div>
                                          </div>
                                        </div> */}

                                        <div className="col-12 col-md-4">
                                          <div className="card border-0 bg-warning-subtle h-100">
                                            <div className="card-body py-3">
                                              <small className="text-warning fw-semibold d-block">
                                                Difference
                                              </small>

                                              <h6 className="text-warning fw-bold mb-0">
                                                {/* ₹{Math.max(newBatchFee - paidFee, 0)} */}
                                                ₹{newBatchFee - paidFee}
                                              </h6>
                                            </div>
                                          </div>
                                        </div>

                                      </div>

                                      {/* ================= Fee Option ================= */}
                                      <div className="mt-4">

                                        <label className="form-label fw-semibold">
                                          <i className="ri-money-rupee-circle-line me-1 text-primary"></i>
                                          Fee Transfer Option
                                        </label>

                                        <div className="row g-3">

                                          {/* OLD FEE */}
                                          <div className="col-12 col-md-6">
                                            <label
                                              className={`card h-100 p-3 ${keepOldFee
                                                ? "border-primary bg-primary-subtle"
                                                : "border"
                                                }`}
                                              style={{ cursor: "pointer" }}
                                            >
                                              <div className="d-flex align-items-start">

                                                <input
                                                  type="radio"
                                                  className="form-check-input me-3 mt-1"
                                                  name="fee_option"
                                                  checked={keepOldFee}
                                                  onChange={() => setKeepOldFee(true)}
                                                />

                                                <div>
                                                  <h6 className="fw-semibold mb-1">
                                                    Keep Old Batch Fee
                                                  </h6>

                                                  <small className="text-muted">
                                                    Old Batch Fee: ₹{oldBatchFee}
                                                  </small>

                                                  <br />

                                                  <small className="text-success">
                                                    Student's existing fee will continue.
                                                  </small>
                                                </div>

                                              </div>
                                            </label>
                                          </div>

                                          {/* NEW FEE */}
                                          <div className="col-12 col-md-6">
                                            <label
                                              className={`card h-100 p-3 ${!keepOldFee
                                                ? "border-success bg-success-subtle"
                                                : "border"
                                                }`}
                                              style={{ cursor: "pointer" }}
                                            >
                                              <div className="d-flex align-items-start">

                                                <input
                                                  type="radio"
                                                  className="form-check-input me-3 mt-1"
                                                  name="fee_option"
                                                  checked={!keepOldFee}
                                                  onChange={() => setKeepOldFee(false)}
                                                />

                                                <div>
                                                  <h6 className="fw-semibold mb-1">
                                                    Use New Batch Fee
                                                  </h6>

                                                  <small className="text-muted">
                                                    New Batch Fee: ₹{newBatchFee}
                                                  </small>

                                                  <br />

                                                  <small className="text-success">
                                                    Destination batch fee will be applied.
                                                  </small>
                                                </div>

                                              </div>
                                            </label>
                                          </div>

                                        </div>
                                      </div>

                                      {/* ================= Validation ================= */}
                                      {!keepOldFee && paidMoreThanNewFee && (
                                        <div className="alert alert-danger mt-3 mb-0 d-flex align-items-start gap-2">
                                          <i className="ri-error-warning-line fs-5"></i>

                                          <div>
                                            <strong>Fee Transfer Not Allowed</strong>

                                            <div className="small mt-1">
                                              Student has already paid ₹{paidFee}, but the new batch
                                              fee is only ₹{newBatchFee}.
                                            </div>

                                            <div className="small mt-1">
                                              Paid fee cannot be greater than the new batch fee.
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
                                      <small className="fw-semibold">
                                        <i className="ri-file-list-3-line me-1 text-primary"></i>
                                        Paid Fee Types
                                      </small>

                                      {paidFeeTypes.length > 0 ? (
                                        <span className="badge bg-success">
                                          {paidFeeTypes.length} Paid Type
                                        </span>
                                      ) : (
                                        <span className="badge bg-secondary">
                                          No Paid Fee
                                        </span>
                                      )}
                                    </div>

                                    {paidFeeTypes.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-2">
                                        {paidFeeTypes.map((type) => {
                                          const available = newBatchFeeTypes.includes(type);

                                          return (
                                            <span
                                              key={type}
                                              className={`badge ${!keepOldFee && !available
                                                ? "bg-danger-subtle text-danger"
                                                : "bg-success-subtle text-success"
                                                }`}
                                            >
                                              <i
                                                className={`${!keepOldFee && !available
                                                  ? "ri-close-circle-line"
                                                  : "ri-checkbox-circle-line"
                                                  } me-1`}
                                              ></i>

                                              {type}

                                              {!keepOldFee
                                                ? available
                                                  ? " — Available"
                                                  : " — Missing"
                                                : " — Existing"}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <small className="text-muted">
                                        Student has no paid fee type.
                                      </small>
                                    )}

                                    {/* New Batch Fee validation */}
                                    {!keepOldFee && hasMissingPaidFeeType && (
                                      <div className="alert alert-danger mt-3 mb-0 py-2">
                                        <div className="d-flex align-items-start gap-2">
                                          <i className="ri-error-warning-line fs-5"></i>

                                          <div>
                                            <strong>Fee Type Missing</strong>

                                            <div className="small mt-1">
                                              The following paid fee type(s) are not available in the new batch:
                                            </div>

                                            <div className="small fw-semibold mt-1">
                                              {missingPaidFeeTypes.join(", ")}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {!keepOldFee && hasFeeTypeAmountError && (
                                      <div className="alert alert-danger mt-3 mb-0 py-2">
                                        <div className="d-flex align-items-start gap-2">
                                          <i className="ri-error-warning-line fs-5"></i>

                                          <div>
                                            <strong>Fee Amount Validation</strong>

                                            <div className="small mt-1">
                                              Student has already paid more than the corresponding fee
                                              amount in the new batch.
                                            </div>

                                            <div className="mt-2">
                                              {feeTypeAmountErrors.map((fee) => (
                                                <div
                                                  key={fee.fee_type}
                                                  className="small fw-semibold text-danger"
                                                >
                                                  {fee.fee_type} — Paid ₹
                                                  {fee.paid_amount.toFixed(2)}
                                                  {" → "}
                                                  New Batch ₹
                                                  {fee.new_amount.toFixed(2)}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {(() => {
                                  if (!filters2?.batch_id) return null;
                                  const batch = batches?.find(
                                    (f) => f.id === Number(filters2?.batch_id)
                                  );
                                  if (!batch) return null;
                                  return (
                                    <>
                                      <input
                                        type="hidden"
                                        value={batch?.course?.id}
                                        name="course_id"
                                      />
                                      <input
                                        type="hidden"
                                        name="fee_option"
                                        value={feeOption}
                                      />
                                      <input
                                        type="hidden"
                                        value={student?.id ?? ""}
                                        name="batch_n_fee_id"
                                      />
                                      <input
                                        type="hidden"
                                        value={student?.student_id ?? ""}
                                        name="student_id"
                                      />
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>



                          {/* --------------------- Additional Fields --------------------- */}
                          <div className="card shadow-sm border-0 rounded-3 mb-4">
                            <div className="card-header bg-light d-flex align-items-center gap-2 py-3">
                              <i className="ri-file-edit-line fs-5"></i>
                              <h6 className="mb-0 fw-semibold">Additional Details</h6>
                            </div>

                            <div className="card-body">

                              {/* Subjects */}
                              {destinationBatch?.course?.subjects_data?.length > 0 && (
                                <>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-semibold mb-0">
                                      <i className="ri-book-open-line me-2 text-primary"></i>
                                      Subjects <span className="text-danger">*</span>
                                    </h6>

                                    <span className="badge bg-primary">
                                      {selectedSubjects.length} / {destinationBatch.course.subjects_data.length} Selected
                                    </span>
                                  </div>

                                  <div className="row g-3 mb-3">
                                    {destinationBatch.course.subjects_data.map((subject) => {
                                      const checked = selectedSubjects.includes(subject.id);

                                      return (
                                        <div className="col-lg-4 col-md-6" key={subject.id}>
                                          <label
                                            className={`card border rounded-3 p-3 h-100 ${checked
                                              ? "border-primary  bg-opacity-10"
                                              : "border-light"
                                              }`}
                                            style={{ cursor: "pointer" }}
                                          >
                                            <div className="d-flex align-items-start">
                                              <input
                                                type="checkbox"
                                                className="form-check-input me-3 mt-1"
                                                checked={checked}
                                                onChange={() => handleSubjectToggle(subject.id)}
                                              />

                                              <div>
                                                <h6 className="mb-1 fw-bold">{subject.name}</h6>
                                                <small className="text-muted">
                                                  Code : {subject.subject_code}
                                                </small>
                                              </div>
                                            </div>
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Hidden Inputs */}
                                  {selectedSubjects.map((id) => (
                                    <input
                                      key={id}
                                      type="hidden"
                                      name="subject_ids[]"
                                      value={id}
                                    />
                                  ))}

                                  {selectedSubjects.length === 0 && (
                                    <div className="text-danger small">
                                      Please select at least one subject.
                                    </div>
                                  )}
                                </>
                              )}


                              <div className="row g-3">
                                <div className="col-12">
                                  <label htmlFor="Transfer_Reason_remark" className="form-label fw-medium">
                                    Transfer Reason / Remark
                                  </label>
                                  <textarea
                                    id="Transfer_Reason_remark"
                                    name="reason"
                                    className="form-control"
                                    rows={3}
                                    placeholder="Enter transfer reason or remark"
                                    required
                                  ></textarea>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* --------------------- Confirmation Warning --------------------- */}
                          {student?.id && filters2?.batch_id && (
                            <div className="alert alert-warning d-flex align-items-start gap-2 rounded-3">
                              <i className="ri-alert-line fs-5"></i>
                              <div>
                                You are transferring this student to another batch. Please
                                verify all details before continuing.
                              </div>
                            </div>
                          )}

                          {/* --------------------- Action Buttons --------------------- */}
                          <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">

                            <button
                              type="button"
                              className="btn btn-outline-warning"
                              onClick={() => {
                                formRef.current?.reset();
                                setFilters2({});
                              }}
                            >
                              <i className="ri-refresh-line me-1"></i>
                              Reset
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={
                                !student?.id ||
                                !filters2?.batch_id ||
                                selectedSubjects.length === 0 ||
                                (!keepOldFee &&
                                  (
                                    paidMoreThanNewFee ||
                                    hasMissingPaidFeeType ||
                                    hasFeeTypeAmountError
                                  ))
                              }
                            >
                              <i className="ri-save-line me-1"></i>
                              Save Transfer
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}